import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  aportarInfo,
  aprobarExpediente,
  notificarExpediente,
  rechazarExpediente,
  reformularCarta,
  solicitarCambioDecision,
} from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { Dialog } from "@/components/ui/Dialog";
import type { DecisionLegal, ExpedienteAnalizado } from "@/types";

interface Props {
  expediente: ExpedienteAnalizado;
}

const DECISIONES_PROPUESTAS: DecisionLegal[] = [
  "ACCEDE",
  "ACCEDE_PARCIAL",
  "NO_ACCEDE",
  "IMPROCEDENTE",
];

const SECCIONES_CARTA = [
  "encabezado",
  "considerandos",
  "parte_resolutiva",
  "notificacion_recursos",
];

export function AccionesBar({ expediente }: Props) {
  const { sesion, puedeAprobar, puedeNotificar } = useAuth();
  const qc = useQueryClient();
  const cerrado =
    expediente.estado === "APROBADO" ||
    expediente.estado === "RECHAZADO" ||
    expediente.estado === "NOTIFICADO";
  const aprobado = expediente.estado === "APROBADO";

  const [aportarOpen, setAportarOpen] = useState(false);
  const [cambioOpen, setCambioOpen] = useState(false);
  const [reformularOpen, setReformularOpen] = useState(false);
  const [rechazoOpen, setRechazoOpen] = useState(false);
  const [notificarOpen, setNotificarOpen] = useState(false);

  const onSuccess = (data: ExpedienteAnalizado) => {
    qc.setQueryData(["expediente", data.expediente_id], data);
    qc.invalidateQueries({ queryKey: ["expedientes"] });
  };

  const aprobar = useMutation({
    mutationFn: (motivo: string) =>
      aprobarExpediente(expediente.expediente_id, sesion?.sub ?? "anon", motivo),
    onSuccess,
  });

  // F3: capacidades por rol (jerarquía admin ⊇ supervisor ⊇ analista).
  // Aprobar / Rechazar / Notificar son del supervisor o admin.
  // Aportar / Cambio decisión / Reformular se hacen desde el chat
  // conversacional (todos los roles humanos pueden).
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 8,
        alignItems: "center",
      }}
    >
      {puedeAprobar() ? (
        <>
          <button
            onClick={() => aprobar.mutate("Aprobado por el supervisor.")}
            disabled={cerrado || aprobar.isPending}
            title="Aprobar el expediente y emitirlo"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "0 16px",
              height: 34,
              background: "var(--emerald)",
              color: "white",
              border: 0,
              borderRadius: "var(--r-sm)",
              fontSize: 13,
              fontWeight: 600,
              cursor: cerrado ? "not-allowed" : "pointer",
              opacity: cerrado || aprobar.isPending ? 0.5 : 1,
              boxShadow: "0 1px 2px oklch(0.30 0.08 162 / 0.25)",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 8l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {aprobar.isPending ? "Aprobando…" : "Aprobar"}
          </button>
          <button
            onClick={() => setRechazoOpen(true)}
            disabled={cerrado}
            className="btn btn--danger"
          >
            Rechazar
          </button>
          {puedeNotificar() && aprobado && (
            <button
              onClick={() => setNotificarOpen(true)}
              title="Notificar al cliente la decisión final (estado terminal)"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "0 16px",
                height: 34,
                background: "var(--ink)",
                color: "white",
                border: 0,
                borderRadius: "var(--r-sm)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M2 4l6 5 6-5M2 4v8h12V4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Notificar al cliente
            </button>
          )}
        </>
      ) : (
        <span
          className="muted"
          style={{ fontSize: 12, fontStyle: "italic" }}
        >
          Aprobar / rechazar / notificar requieren rol supervisor o
          administrador.
        </span>
      )}

      {/* Dialog de rechazo conservado: requiere motivo obligatorio que
          se registra en audit log SSPD. */}
      <DialogAportar
        abierto={aportarOpen}
        onCerrar={() => setAportarOpen(false)}
        expedienteId={expediente.expediente_id}
        autor={sesion?.sub ?? "anon"}
        onSuccess={onSuccess}
      />
      <DialogCambioDecision
        abierto={cambioOpen}
        onCerrar={() => setCambioOpen(false)}
        expedienteId={expediente.expediente_id}
        autor={sesion?.sub ?? "anon"}
        onSuccess={onSuccess}
      />
      <DialogReformular
        abierto={reformularOpen}
        onCerrar={() => setReformularOpen(false)}
        expedienteId={expediente.expediente_id}
        autor={sesion?.sub ?? "anon"}
        onSuccess={onSuccess}
      />
      <DialogRechazo
        abierto={rechazoOpen}
        onCerrar={() => setRechazoOpen(false)}
        expedienteId={expediente.expediente_id}
        revisor={sesion?.sub ?? "anon"}
        onSuccess={onSuccess}
      />
      <DialogNotificar
        abierto={notificarOpen}
        onCerrar={() => setNotificarOpen(false)}
        expedienteId={expediente.expediente_id}
        revisor={sesion?.sub ?? "anon"}
        onSuccess={onSuccess}
      />
    </div>
  );
}

// ─── Dialogs ──────────────────────────────────────────────────────── //

interface DialogProps {
  abierto: boolean;
  onCerrar: () => void;
  expedienteId: string;
  autor: string;
  onSuccess: (e: ExpedienteAnalizado) => void;
}

function DialogAportar({
  abierto,
  onCerrar,
  expedienteId,
  autor,
  onSuccess,
}: DialogProps) {
  const [json, setJson] = useState('{\n  "lectura_factura": 39887\n}');
  const [justificacion, setJustificacion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      let deltas: Record<string, unknown>;
      try {
        deltas = JSON.parse(json);
      } catch {
        throw new Error("El JSON de deltas no es válido.");
      }
      return aportarInfo(expedienteId, deltas, autor, justificacion);
    },
    onSuccess: (data) => {
      onSuccess(data);
      onCerrar();
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <Dialog
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Aportar información"
      acciones={
        <>
          <button onClick={onCerrar} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={() => mut.mutate()}
            className="btn-primary"
            disabled={mut.isPending}
          >
            {mut.isPending ? "Reprocesando…" : "Aportar y reprocesar"}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        Pega los campos del PQR a actualizar como JSON. GEMA reprocesará la
        decisión y la carta.
      </p>
      <label className="label">Deltas (JSON)</label>
      <textarea
        value={json}
        onChange={(e) => setJson(e.target.value)}
        className="input font-mono text-xs"
        rows={8}
      />
      <label className="label">Justificación</label>
      <textarea
        value={justificacion}
        onChange={(e) => setJustificacion(e.target.value)}
        className="input"
        rows={3}
        placeholder="Encontré la OS-22815 en SGC tras búsqueda manual."
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </Dialog>
  );
}

function DialogCambioDecision({
  abierto,
  onCerrar,
  expedienteId,
  autor,
  onSuccess,
}: DialogProps) {
  const [decision, setDecision] = useState<DecisionLegal>("NO_ACCEDE");
  const [justificacion, setJustificacion] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      solicitarCambioDecision(expedienteId, decision, justificacion, autor),
    onSuccess: (data) => {
      onSuccess(data);
      onCerrar();
    },
  });

  return (
    <Dialog
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Solicitar cambio de decisión"
      acciones={
        <>
          <button onClick={onCerrar} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={() => mut.mutate()}
            className="btn-primary"
            disabled={mut.isPending || !justificacion.trim()}
          >
            {mut.isPending ? "Enviando…" : "Enviar a supervisor"}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        El expediente quedará <strong>EN_AJUSTE</strong> para validación del
        supervisor. GEMA no aplica la nueva decisión hasta que un supervisor lo
        apruebe explícitamente.
      </p>
      <label className="label">Decisión propuesta</label>
      <select
        value={decision}
        onChange={(e) => setDecision(e.target.value as DecisionLegal)}
        className="input"
      >
        {DECISIONES_PROPUESTAS.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
      <label className="label">Justificación</label>
      <textarea
        value={justificacion}
        onChange={(e) => setJustificacion(e.target.value)}
        className="input"
        rows={4}
        required
      />
    </Dialog>
  );
}

function DialogReformular({
  abierto,
  onCerrar,
  expedienteId,
  autor,
  onSuccess,
}: DialogProps) {
  const [seccion, setSeccion] = useState("encabezado");
  const [instruccion, setInstruccion] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      reformularCarta(expedienteId, seccion, instruccion, autor),
    onSuccess: (data) => {
      onSuccess(data);
      onCerrar();
    },
  });

  return (
    <Dialog
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Reformular sección de la carta"
      acciones={
        <>
          <button onClick={onCerrar} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={() => mut.mutate()}
            className="btn-primary"
            disabled={mut.isPending || !instruccion.trim()}
          >
            {mut.isPending ? "Reformulando…" : "Reformular"}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        El LLM ajustará sólo la sección elegida. La decisión jurídica no cambia.
      </p>
      <label className="label">Sección</label>
      <select
        value={seccion}
        onChange={(e) => setSeccion(e.target.value)}
        className="input"
      >
        {SECCIONES_CARTA.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <label className="label">Instrucción para el LLM</label>
      <textarea
        value={instruccion}
        onChange={(e) => setInstruccion(e.target.value)}
        className="input"
        rows={4}
        placeholder="Más formal, sin abreviaturas; cita textual del Art. 149."
      />
    </Dialog>
  );
}

function DialogNotificar({
  abierto,
  onCerrar,
  expedienteId,
  revisor,
  onSuccess,
}: {
  abierto: boolean;
  onCerrar: () => void;
  expedienteId: string;
  revisor: string;
  onSuccess: (e: ExpedienteAnalizado) => void;
}) {
  const [motivo, setMotivo] = useState("Notificación al cliente.");
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => notificarExpediente(expedienteId, revisor, motivo),
    onSuccess: (data) => {
      onSuccess(data);
      onCerrar();
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <Dialog
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Notificar al cliente"
      acciones={
        <>
          <button onClick={onCerrar} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={() => mut.mutate()}
            className="btn-primary"
            disabled={mut.isPending}
          >
            {mut.isPending ? "Notificando…" : "Confirmar notificación"}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        Esta acción marca el expediente como <strong>NOTIFICADO</strong> y lo
        retira del flujo activo. Asegúrate de que la carta definitiva ya fue
        enviada al cliente.
      </p>
      <label className="label">Observación (opcional)</label>
      <textarea
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        className="input"
        rows={3}
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </Dialog>
  );
}

function DialogRechazo({
  abierto,
  onCerrar,
  expedienteId,
  revisor,
  onSuccess,
}: {
  abierto: boolean;
  onCerrar: () => void;
  expedienteId: string;
  revisor: string;
  onSuccess: (e: ExpedienteAnalizado) => void;
}) {
  const [motivo, setMotivo] = useState("");
  const mut = useMutation({
    mutationFn: () => rechazarExpediente(expedienteId, revisor, motivo),
    onSuccess: (data) => {
      onSuccess(data);
      onCerrar();
    },
  });

  return (
    <Dialog
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Rechazar expediente"
      acciones={
        <>
          <button onClick={onCerrar} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={() => mut.mutate()}
            className="btn-danger"
            disabled={mut.isPending || !motivo.trim()}
          >
            {mut.isPending ? "Rechazando…" : "Rechazar"}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        El expediente se cierra como <strong>RECHAZADO</strong>. El motivo es
        obligatorio y queda registrado en el historial.
      </p>
      <label className="label">Motivo del rechazo</label>
      <textarea
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        className="input"
        rows={4}
        required
      />
    </Dialog>
  );
}
